import { Button, Input, Label, Textarea } from "@/components/ui";
import { ListOrdered } from "lucide-react";
import { useRef, useState } from "react";
import type { SurveyDraftSection } from "../../../../shared/survey-questions";
import { ReorderSurveySectionsModal } from "./ReorderSurveySectionsModal";

export type { SurveyDraftSection } from "../../../../shared/survey-questions";

export function SurveySectionEditor({
  sections,
  usedSectionIds,
  disabled,
  showValidationErrors,
  onChange,
}: {
  sections: SurveyDraftSection[];
  usedSectionIds: Set<string>;
  disabled: boolean;
  showValidationErrors: boolean;
  onChange: (sections: SurveyDraftSection[]) => void;
}) {
  const [reorderOpen, setReorderOpen] = useState(false);
  const reorderTriggerRef = useRef<HTMLButtonElement>(null);
  const update = (index: number, patch: Partial<SurveyDraftSection>) => {
    onChange(
      sections.map((section, i) =>
        i === index ? { ...section, ...patch } : section,
      ),
    );
  };
  return (
    <div className="space-y-3 rounded-xl border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Label>Sections</Label>
        <div className="flex gap-2">
          {sections.length > 1 ? (
            <Button
              ref={reorderTriggerRef}
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => {
                setReorderOpen(true);
              }}
            >
              <ListOrdered className="size-4" /> Reorder sections
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={() => {
              const id = crypto.randomUUID();
              onChange([
                ...sections,
                {
                  id,
                  key: id,
                  title: "",
                  description: null,
                  position: sections.length,
                },
              ]);
            }}
          >
            {sections.length ? "Add section" : "Organize into sections"}
          </Button>
        </div>
      </div>
      {sections.map((section, index) => {
        const invalidTitle = showValidationErrors && !section.title.trim();
        return (
          <div key={section.key} className="space-y-3 border-t pt-3">
            <div className="space-y-2">
              <Label htmlFor={`section-${section.key}-title`}>
                Section {index + 1} title *
              </Label>
              <Input
                id={`section-${section.key}-title`}
                value={section.title}
                disabled={disabled}
                aria-invalid={invalidTitle}
                aria-describedby={
                  invalidTitle
                    ? `section-${section.key}-title-error`
                    : undefined
                }
                onChange={(event) => {
                  update(index, { title: event.target.value });
                }}
              />
              {invalidTitle ? (
                <p
                  id={`section-${section.key}-title-error`}
                  role="alert"
                  className="text-destructive text-sm"
                >
                  Section title cannot be blank.
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor={`section-${section.key}-description`}>
                Section {index + 1} description
              </Label>
              <Textarea
                id={`section-${section.key}-description`}
                value={section.description ?? ""}
                disabled={disabled}
                onChange={(event) => {
                  update(index, { description: event.target.value || null });
                }}
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={disabled || usedSectionIds.has(section.id)}
              title={
                usedSectionIds.has(section.id)
                  ? "Move or unassign this section's questions before removing it"
                  : undefined
              }
              onClick={() => {
                onChange(
                  sections
                    .filter((_, i) => i !== index)
                    .map((s, position) => ({ ...s, position })),
                );
              }}
            >
              Remove empty section
            </Button>
          </div>
        );
      })}
      {reorderOpen ? (
        <ReorderSurveySectionsModal
          sections={sections}
          onClose={() => {
            setReorderOpen(false);
          }}
          onSave={onChange}
          onRestoreFocus={() => reorderTriggerRef.current?.focus()}
        />
      ) : null}
    </div>
  );
}
